// MAJLIS-71, the owner's phone test (UI-C19, UI-C10, EM-12; its T- row is the consultant's to number): a recorder of
// what the microphone, the page and the LiveKit connection DO, for the flag-gated `?micdiag=1` panel (micdiag.tsx).
// On an iPhone with no Mac attached the console cannot be read, so the owner copies this log into the chat instead.
//
// NUMBERS AND EVENT NAMES ONLY, NEVER AUDIO (consultant ruling Q5, 2026-09-24). What makes that true is the SHAPE of
// this module, not the care of its callers:
// - an event name is one of the fixed EVENTS below and a field key one of the fixed KEYS (TypeScript unions);
// - a field value is a number, a boolean, or a string that is printed ONLY IF it is one of the fixed WORDS - every other
//   string prints as "other". So a room code, a token, a device id or label, a user agent or an error message cannot
//   reach the text even if a caller passes one;
// - it never reads a track's id or label, and never touches a sample of audio: tracks are numbered 1, 2, ... in the
//   order they are seen. It is handed no room code, no URL and no grant. MAJLIS-116 (T-141): it DOES read a track's
//   getSettings(), but restricted to exactly six keys - autoGainControl, echoCancellation, noiseSuppression,
//   voiceIsolation, sampleRate, channelCount - never deviceId, groupId or any other string setting.
// micDiag.test.ts plants a room code, a token-shaped string and a device label in every recorded event and asserts
// none of them is in the copied text, beside the control that the same events ARE there by name.
//
// It only OBSERVES: it adds event listeners and reads properties; it never writes a track, a context or the document.
// Q3 of the ruling: for each `mute` -> `unmute` pair of the track it prints the muted duration (mutedMs), so the owner
// test shows whether a muted-but-live track resumes on its own.
//
// MAJLIS-116 (T-141, UI-C19, EM-12): the owner's phone test also needs to see what Safari APPLIED to his microphone
// and how his level moves. At mic open it prints ONE line of the six settings above (an absent key prints "n/a", the
// one non-numeric, non-boolean word this module ever derives from a setting). Once a second it prints the UI-C19
// ring's own RMS as dBFS and its high/low against MIC_HIGH_LEVEL (constants.ts) - the SAME RMS the ring already
// computes (watchLevel's source, never a second AnalyserNode, never a sample of audio read here) - beside this
// second's count of speech_start events for THIS seat (App.tsx calls speechStart(), already filtered to its own
// seat id: micDiag is handed no seat id to filter with itself).
//
// MAJLIS-116 (T-141) also gives it two connection-loss lines, each an observation only, never read off argv or a
// log body: LiveKit's Disconnected reason (livekitRoom.ts's own fixed DISCONNECT_REASONS table - never a bare read
// of the library's enum) beside the page's visibility at that moment, and the room socket's own close code and
// whether it was clean (protocol/client.ts's SocketObserver) - never LiveKit's close, which is the line above.
//
// MAJLIS-116 (T-147, DIAGNOSTIC ONLY, no product default changes): a "mic requested" line, one per mic open (micdiag.tsx
// records it, right before the opener runs), saying which constraint set `?micdiag=1&ec=0` made this open request -
// ec=off or ec=on - fixed words, never a number of anything else derived from a real constraint object. This is NOT
// the "mic settings" line above: that one is what Safari APPLIED (getSettings(), after the fact); this one is what
// was ASKED FOR, before the call.
import { MIC_HIGH_LEVEL } from "../constants";

/** ASSUMPTION (the consultant's example, not a measurement): the last 500 lines are kept; older lines are dropped and
 * counted in the header. */
export const MICDIAG_MAX_LINES = 500;

/** ASSUMPTION: how often the track's readyState/enabled/muted and the AudioContext's state are re-read, on the injected
 * clock. `enabled` has no event (the gate writes it, EM-25), and a track stopped by our own code fires no `ended`, so
 * those changes are seen only by reading. A change is printed once, when it is first seen; nothing is printed while
 * nothing changes. */
export const MICDIAG_SAMPLE_MS = 250;

/** MAJLIS-116 (T-141, UI-C19): how often the ring's level line prints, on the injected clock. ASSUMPTION: once a
 * second is enough to see his level move; nothing prints before the ring has a level source (sampleLevel is a no-op
 * until watchLevel is given one - the microphone is not open yet). At this one-line-a-second rate the MICDIAG_MAX_LINES
 * buffer above (500) holds about 500 / 60 ≈ 8 minutes before the oldest lines drop. */
export const MICDIAG_LEVEL_MS = 1_000;

/** MAJLIS-116 (T-141): dBFS has no floor at rms=0 (20*log10(0) = -Infinity). ASSUMPTION, not a measurement: -100
 * dBFS stands in for silence, an ordinary meter floor. */
export const MICDIAG_DBFS_FLOOR = -100;
const MIN_RMS = 10 ** (MICDIAG_DBFS_FLOOR / 20);

/** The event names a line can carry. */
export type DiagEvent =
  | "micdiag on"
  | "gum call"
  | "gum ok"
  | "gum error"
  | "mic opened"
  | "track attached"
  | "track ended"
  | "track mute"
  | "track unmute"
  | "track change"
  | "visibilitychange"
  | "focus"
  | "blur"
  | "pagehide"
  | "pageshow"
  | "audioctx attached"
  | "audioctx statechange"
  | "audioctx resume"
  | "lk connection"
  | "lk local track published"
  | "lk local track unpublished"
  | "lk local track muted"
  | "lk local track unmuted"
  | "lk disconnected"
  | "socket close"
  | "mic settings"
  | "level"
  // MAJLIS-116 (T-147, DIAGNOSTIC ONLY): the constraint set requested at mic open, ec=off or ec=on.
  | "mic requested";

/** The field keys a line can carry. */
export type DiagKey =
  | "track"
  | "tracks"
  | "readyState"
  | "enabled"
  | "muted"
  | "mutedMs"
  | "whileMuted"
  | "visibility"
  | "focused"
  | "persisted"
  | "state"
  | "trigger"
  | "error"
  | "permission"
  | "maxLines"
  | "sampleMs"
  // MAJLIS-116 (T-141): the track's getSettings(), restricted to exactly these six keys - never deviceId, groupId,
  // label or any other string setting.
  | "autoGainControl"
  | "echoCancellation"
  | "noiseSuppression"
  | "voiceIsolation"
  | "sampleRate"
  | "channelCount"
  // MAJLIS-116 (T-141): the once-a-second level line.
  | "dBFS"
  | "high"
  | "speechStarts"
  // MAJLIS-116 (T-141): LiveKit's Disconnected reason, and the room socket's own close.
  | "reason"
  | "code"
  | "clean"
  // MAJLIS-116 (T-147, DIAGNOSTIC ONLY): the "mic requested" line's one field, "off" or "on".
  | "ec";

/** Every string a line may print. Anything else prints as "other". */
const WORDS: ReadonlySet<string> = new Set([
  // MediaStreamTrack.readyState
  "live",
  "ended",
  // document.visibilityState
  "visible",
  "hidden",
  "prerender",
  // AudioContext.state (WebKit adds "interrupted")
  "running",
  "suspended",
  "closed",
  "interrupted",
  // MAJLIS-98: what tried the ring's resume() (ringResume.ts ResumeTrigger; "visible" is above)
  "focus",
  "unmute",
  "gesture",
  // livekit-client ConnectionState
  "disconnected",
  "connecting",
  "connected",
  "reconnecting",
  "signalReconnecting",
  // MicPermission
  "pending",
  "granted",
  "denied",
  "unavailable",
  // getUserMedia error names (MDN), plus our own "no mediaDevices" and "not an Error"
  "AbortError",
  "InvalidStateError",
  "NotAllowedError",
  "NotFoundError",
  "NotReadableError",
  "OverconstrainedError",
  "SecurityError",
  "TypeError",
  "Unsupported",
  "Error",
  "none",
  // MAJLIS-116 (T-141): a getSettings() key the browser did not return at all (never confused with a real false/0).
  "n/a",
  // MAJLIS-116 (T-141): livekit-client's DisconnectReason (livekitRoom.ts's DISCONNECT_REASONS - the fixed table
  // this codebase owns; anything else is "other").
  "unknownReason",
  "clientInitiated",
  "duplicateIdentity",
  "serverShutdown",
  "participantRemoved",
  "roomDeleted",
  "stateMismatch",
  "joinFailure",
  "migration",
  "signalClose",
  "roomClosed",
  "userUnavailable",
  "userRejected",
  "sipTrunkFailure",
  // MAJLIS-116 (T-147, DIAGNOSTIC ONLY): the "mic requested" line's ec value - never "true"/"false" (word() would
  // print those for a real boolean; this is deliberately its own pair of words, the request, not a setting).
  "off",
  "on",
]);

export type DiagValue = number | boolean | string | null;

/** A value as the log prints it: numbers rounded to whole units, strings only from WORDS. */
export function word(v: DiagValue): string {
  if (v === null) return "none";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") return Number.isFinite(v) ? String(Math.round(v)) : "nan";
  return WORDS.has(v) ? v : "other";
}

/** The part of a MediaStreamTrack this reads: its state, its three events, and (MAJLIS-116) its getSettings() -
 * never its id or label. */
export type WatchedTrack = Pick<MediaStreamTrack, "readyState" | "enabled" | "muted" | "addEventListener" | "removeEventListener" | "getSettings">;

/** The part of an AudioContext this reads. */
export type WatchedContext = Pick<AudioContext, "state" | "addEventListener" | "removeEventListener">;

/** MAJLIS-116 (T-141): getSettings(), narrowed to exactly the six keys this module ever prints. `voiceIsolation` is
 * added by hand, the same trick as mic.ts's AudioConstraints: TypeScript 5.7's lib.dom.d.ts has no such member
 * (checked 2026-09-22), though a supporting browser can return it. */
type WatchedSettings = Pick<MediaTrackSettings, "autoGainControl" | "echoCancellation" | "noiseSuppression" | "sampleRate" | "channelCount"> & {
  voiceIsolation?: boolean;
};

/** MAJLIS-116 (T-141, UI-C19): the ring's own level - mic.ts's MicSource.level(), an RMS 0..1 already computed by
 * its one AnalyserNode. Never a second analyser, never a sample of audio read here. */
export interface LevelSource {
  level(): number;
}

/** A getSettings() value as the "mic settings" line prints it: present -> its own word; absent (the key was not in
 * the object at all, not merely falsy) -> "n/a". */
function settingWord(v: boolean | number | undefined): DiagValue {
  return v === undefined ? "n/a" : v;
}

interface Snapshot {
  readyState: string;
  enabled: boolean;
  muted: boolean;
}

export class MicDiag {
  private buf: string[] = [];
  private dropped = 0;
  private listeners = new Set<() => void>();
  private ver = 0;

  private track: WatchedTrack | null = null;
  private trackNo = 0;
  private tracksSeen = 0;
  private snap: Snapshot | null = null;
  private mutedAt: number | null = null;
  private untrack: (() => void) | null = null;

  private ctx: WatchedContext | null = null;
  private ctxState: string | null = null;
  private unctx: (() => void) | null = null;

  // MAJLIS-116 (T-141): the ring's level source and this second's speech_start count for this seat.
  private levelSrc: LevelSource | null = null;
  private speechStarts = 0;

  constructor(
    /** ms since page load (performance.now() through the app's clock; a test's FakeClock). */
    private readonly now: () => number,
    private readonly max = MICDIAG_MAX_LINES,
  ) {}

  // ------------------------------------------------------------ the log

  record(event: DiagEvent, fields: Partial<Record<DiagKey, DiagValue>> = {}): void {
    const t = Math.max(0, Math.round(this.now()));
    let line = `+${t}ms ${event}`;
    for (const [k, v] of Object.entries(fields)) line += ` ${k}=${word(v as DiagValue)}`;
    this.buf.push(line);
    if (this.buf.length > this.max) {
      this.dropped += this.buf.length - this.max;
      this.buf.splice(0, this.buf.length - this.max);
    }
    this.ver += 1;
    for (const fn of this.listeners) fn();
  }

  lines(): readonly string[] {
    return this.buf;
  }

  /** What "Copy log" copies: one header line, then the kept lines. */
  text(): string {
    const head = `majlis micdiag: times in ms since page load; last ${this.buf.length} of max ${this.max} lines; dropped ${this.dropped}`;
    return [head, ...this.buf].join("\n");
  }

  version = (): number => this.ver;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };

  // ------------------------------------------------------------ the microphone track

  /** The track the app opened (null: none). Listens for ended/mute/unmute on it; stops listening to the previous one. */
  watchTrack(track: WatchedTrack | null): void {
    if (track === this.track) return;
    this.untrack?.();
    this.untrack = null;
    this.track = track;
    this.mutedAt = null;
    this.snap = null;
    if (!track) return;
    this.tracksSeen += 1;
    const no = (this.trackNo = this.tracksSeen);
    this.snap = this.read(track);
    if (track.muted) this.mutedAt = this.now();
    this.record("track attached", { track: no, ...this.snap });
    this.recordSettings(track); // MAJLIS-116 (T-141): once, at mic open (every re-open, D-52, prints its own line)
    const onEnded = () => {
      const f: Partial<Record<DiagKey, DiagValue>> = { track: no };
      if (this.mutedAt !== null) {
        f.whileMuted = true;
        f.mutedMs = this.now() - this.mutedAt;
      }
      this.snap = this.read(track);
      this.record("track ended", { ...f, ...this.snap });
    };
    const onMute = () => {
      this.mutedAt = this.now();
      this.snap = this.read(track);
      this.record("track mute", { track: no, ...this.snap });
    };
    const onUnmute = () => {
      // Q3: the duration of this mute -> unmute pair (null when no mute was seen: the track came to us muted-unknown)
      const mutedMs = this.mutedAt === null ? null : this.now() - this.mutedAt;
      this.mutedAt = null;
      this.snap = this.read(track);
      this.record("track unmute", { track: no, mutedMs, ...this.snap });
    };
    track.addEventListener("ended", onEnded);
    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    this.untrack = () => {
      track.removeEventListener("ended", onEnded);
      track.removeEventListener("mute", onMute);
      track.removeEventListener("unmute", onUnmute);
    };
  }

  private read(t: WatchedTrack): Snapshot {
    return { readyState: t.readyState, enabled: t.enabled, muted: t.muted };
  }

  /** MAJLIS-116 (T-141): what Safari APPLIED to the microphone, restricted to exactly these six keys - never
   * deviceId, groupId, label or any other string setting getSettings() might return. */
  private recordSettings(t: WatchedTrack): void {
    const s: WatchedSettings = t.getSettings();
    this.record("mic settings", {
      autoGainControl: settingWord(s.autoGainControl),
      echoCancellation: settingWord(s.echoCancellation),
      noiseSuppression: settingWord(s.noiseSuppression),
      voiceIsolation: settingWord(s.voiceIsolation),
      sampleRate: settingWord(s.sampleRate),
      channelCount: settingWord(s.channelCount),
    });
  }

  // ------------------------------------------------------------ the mic ring's AudioContext

  /** The ring's AudioContext, once it exists (mic.ts makes it on the first open). */
  watchContext(ctx: WatchedContext | null): void {
    if (!ctx || ctx === this.ctx) return;
    this.unctx?.();
    this.ctx = ctx;
    this.ctxState = ctx.state;
    this.record("audioctx attached", { state: ctx.state });
    const onState = () => {
      if (ctx.state === this.ctxState) return;
      this.ctxState = ctx.state;
      this.record("audioctx statechange", { state: ctx.state });
    };
    ctx.addEventListener("statechange", onState);
    this.unctx = () => ctx.removeEventListener("statechange", onState);
  }

  /** Re-read what has no event (the track's enabled; a readyState our own stop() changed; a context state change the
   * event missed) and print each change once. */
  sample(ctx: WatchedContext | null = null): void {
    this.watchContext(ctx);
    const t = this.track;
    if (t && this.snap) {
      const now = this.read(t);
      if (now.readyState !== this.snap.readyState || now.enabled !== this.snap.enabled || now.muted !== this.snap.muted) {
        this.snap = now;
        this.record("track change", { track: this.trackNo, ...now });
      }
    }
    const c = this.ctx;
    if (c && c.state !== this.ctxState) {
      this.ctxState = c.state;
      this.record("audioctx statechange", { state: c.state });
    }
  }

  // ------------------------------------------------------------ the mic ring's level (MAJLIS-116, T-141, UI-C19)

  /** The ring's level source (mic.ts's MicSource, given once the microphone is open); null stops the level lines
   * until a new one is attached. Never a second AnalyserNode: this reads the SAME RMS the ring already computes. */
  watchLevel(source: LevelSource | null): void {
    this.levelSrc = source;
  }

  /** One speech_start for THIS seat (App.tsx: `ev.data.seat === client.seat?.id`, the same filter it already runs
   * for seat_updated - micDiag itself is handed no seat id). Counted into the next level line, then reset. */
  speechStart(): void {
    this.speechStarts += 1;
  }

  /** Once a second (on the injected clock, wired by micdiag.tsx): the ring's own RMS as dBFS, its high/low against
   * MIC_HIGH_LEVEL (EM-12, UI-C19), and this second's speech_start count for this seat. Prints nothing before a
   * level source is attached (the microphone is not open yet); the count still resets every call so a stray
   * speech_start before mic open cannot land on a much later line. */
  sampleLevel(): void {
    const speechStarts = this.speechStarts;
    this.speechStarts = 0;
    if (!this.levelSrc) return;
    const rms = Math.min(1, Math.max(0, this.levelSrc.level()));
    const dBFS = Math.round(20 * Math.log10(Math.max(rms, MIN_RMS)));
    this.record("level", { dBFS, high: rms >= MIC_HIGH_LEVEL, speechStarts });
  }

  // ------------------------------------------------------------ the page

  /** visibilitychange, focus, blur, pagehide, pageshow; returns the detach. */
  watchPage(doc: Document, win: Window): () => void {
    const page = (event: DiagEvent, extra: Partial<Record<DiagKey, DiagValue>> = {}) => () =>
      this.record(event, { visibility: doc.visibilityState, focused: doc.hasFocus(), ...extra });
    const onVis = page("visibilitychange");
    const onFocus = page("focus");
    const onBlur = page("blur");
    const onHide = (e: Event) => page("pagehide", { persisted: (e as PageTransitionEvent).persisted === true })();
    const onShow = (e: Event) => page("pageshow", { persisted: (e as PageTransitionEvent).persisted === true })();
    doc.addEventListener("visibilitychange", onVis);
    win.addEventListener("focus", onFocus);
    win.addEventListener("blur", onBlur);
    win.addEventListener("pagehide", onHide);
    win.addEventListener("pageshow", onShow);
    return () => {
      doc.removeEventListener("visibilitychange", onVis);
      win.removeEventListener("focus", onFocus);
      win.removeEventListener("blur", onBlur);
      win.removeEventListener("pagehide", onHide);
      win.removeEventListener("pageshow", onShow);
    };
  }
}
